import React from "react";
import { Link } from "react-router-dom";
import { Award, Heart, Microscope, Sparkles } from "lucide-react";
import { IMAGES, BRAND } from "../data/site";
import { SectionHeading } from "../components/SectionHeading";
import { Seo } from "../components/Seo";
import { SEO, personSchema, organizationSchema, breadcrumbSchema } from "../data/seo";

const FEATURES = [
  { icon: Award, title: "FindATopDoc Featured Doctor", body: "Recognized as a leading dentist in the New York metro area — featured on FindATopDoc.com." },
  { icon: Heart, title: "Over 20 years in practice", body: "Two decades of refining the craft — from cosmetic and restorative to biomimetic and holistic dentistry." },
  { icon: Microscope, title: "Huggins Institute trained", body: "Safe amalgam removal protocol with full rubber-dam isolation — protecting you and your team." },
  { icon: Sparkles, title: "Family-oriented, spa-style care", body: "Gentle dentistry for adults and children, paired with an in-office wellness studio. Your voice is always heard." },
];

export const AboutPage = () => (
  <div data-testid="about-page">
    <Seo
      title={SEO.about.title}
      description={SEO.about.description}
      image={IMAGES.Drk}
      type="profile"
      schemas={[
        personSchema(),
        organizationSchema(),
        breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }]),
      ]}
    />
    {/* Hero */}
    <section className="relative h-[80vh] overflow-hidden">
      <img src={IMAGES.Drk} alt="Dr. K Portrait" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/30 to-ink/80" />
      <div className="relative h-full container-wide flex flex-col justify-end pb-24 text-white">
        <div className="label-eyebrow text-gold mb-6">About</div>
        <h1 className="heading-display text-5xl md:text-7xl lg:text-8xl max-w-4xl text-white">
          Behind every smile <span className="italic text-gold-light">there is a story.</span>
        </h1>
        <p className="font-serif italic text-xl md:text-2xl text-white/80 mt-6 max-w-2xl">
          Behind every dentist, there is a purpose. - About Dr.K.
        </p>
      </div>
    </section>

    {/* Dr. K Difference */}
    <section className="py-24 md:py-40 container-x">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-32 reveal">
          <div className="label-eyebrow mb-6">The Difference</div>
          <h2 className="heading-display text-4xl md:text-5xl lg:text-6xl">
            Dentistry, but practiced as <span className="italic text-gold">fine art.</span>
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-8 text-ink/80 text-lg leading-[1.8] reveal">
          <p>
            I became a dentist because I wanted to give people something that nothing else could — a reason to smile freely. Not a Hollywood smile. <em>Their</em> smile. The one they were meant to have.
          </p>
          <p>
            For nearly twenty years, I've practiced with one guiding belief: my office should feel like an extension of your home. Welcoming. Comfortable. A place where you come to relax, heal, rejuvenate — and walk out feeling great about yourself and life in general.
          </p>
          <p>
            We provide the best and latest in technology and treatment options, but technology is only as good as the hands that use it. Every restoration is hand-finished. Every plan is built around <em>you</em> — your concerns, your goals, your story. Your voice is always heard.
          </p>
          <p>
            From biomimetic and holistic dentistry to full smile makeovers, dental implants, and our exclusive in-office wellness spa — we're here to care for the whole you. Not just the teeth.
          </p>
          <p className="font-serif italic text-2xl text-gold-dark border-l-2 border-gold pl-6 my-12">
            "A beautiful smile is the architecture of confidence. My job is to build it well."
          </p>
          <p className="text-sm tracking-widest uppercase text-ink/50">— {BRAND.doctor}</p>
        </div>
      </div>
    </section>

    {/* Why Dr. K Feature Grid */}
    <section className="py-24 md:py-32 bg-ink text-bone">
      <div className="container-x">
        <SectionHeading eyebrow="Why Dr. K" title="What you can" italicWord="expect." light />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="bg-ink p-10 md:p-14 reveal" data-testid={`feature-${i}`} style={{ transitionDelay: `${i * 60}ms` }}>
                <Icon size={28} strokeWidth={1} className="text-gold mb-8" />
                <h3 className="font-serif text-2xl md:text-3xl font-light leading-tight">{f.title}</h3>
                <p className="text-white/60 mt-4 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-24 md:py-32 container-x text-center">
      <SectionHeading title="Come" italicWord="meet us." align="center" />
      <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/contact" data-testid="about-book-cta" className="btn-primary">Book a Consultation</Link>
        <Link to="/services" data-testid="about-services-cta" className="btn-outline">View Services</Link>
      </div>
    </section>
  </div>
);
